<template>
  <section class="scrolling-gallery" :style="{ '--scroll-speed': scrollSpeed + 's' }">
    <div class="gallery-columns">
      <div 
        v-for="(column, columnIndex) in imageColumns" 
        :key="columnIndex"
        class="gallery-column"
        :style="{ animationDelay: `${columnIndex * -2}s` }"
      >
        <div class="column-content">
          <div
            v-for="(image, imageIndex) in [...column, ...column]" 
            :key="`${columnIndex}-${imageIndex}-${image.id || imageIndex}`"
            class="gallery-item"
            @click="$emit('imageClick', image, columnIndex, imageIndex)"
            @pointerenter="$emit('imageHover', image)"
          >
            <div class="image-container" :style="{ aspectRatio: image.ratio }">
              <picture>
                <source
                  v-for="s in pictureFor(image).sources"
                  :key="s.type"
                  :type="s.type"
                  :srcset="s.srcset"
                  sizes="(max-width: 768px) 45vw, 30vw"
                />
                <img
                  :src="pictureFor(image).jpgSrc || getImageUrl(image)"
                  sizes="(max-width: 768px) 45vw, 30vw"
                  :alt="image.description"
                  class="gallery-image"
                  loading="lazy"
                />
              </picture>
              <div class="image-overlay" :class="{ 'hover-only': !showLabelsAlways }">
                <span class="image-label">{{ image.title }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { imageService, type GalleryImage } from '@/services/imageService'
import { pictureSourcesFor, type FeedImage, type PictureModel } from '@/services/portfolioService'
import { settingsService } from '@/services/settingsService'

// Accept either the new API-backed FeedImage (with variants + absolute src) or a
// legacy static GalleryImage (resolved through imageService).
type AnyImage = (FeedImage | GalleryImage) & { variants?: FeedImage['variants']; src?: string }

interface Props {
  images: AnyImage[]
  showLabelsAlways?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showLabelsAlways: true
})

defineEmits<{
  imageClick: [image: AnyImage, columnIndex: number, imageIndex: number]
  imageHover: [image: AnyImage]
}>()

// Distribute images across 3 columns
const imageColumns = computed(() => [
  props.images.filter((_, index) => index % 3 === 0),
  props.images.filter((_, index) => index % 3 === 1),
  props.images.filter((_, index) => index % 3 === 2),
])

// Prefer the API-provided src; fall back to the legacy imageService thumbnail.
const getImageUrl = (image: AnyImage): string => {
  if (image.src) return image.src
  return imageService.getThumbnailUrl(image as GalleryImage)
}

// Build an ordered <picture> model from feed variants (AVIF + WebP <source> +
// JPG fallback). Legacy static images have no variants → sources is empty and the
// <img> falls back to getImageUrl(image).
const pictureFor = (image: AnyImage): PictureModel =>
  pictureSourcesFor(image.variants, getImageUrl(image))

const settings = settingsService.getSettings()
const scrollSpeed = settings.homepage.scrollSpeed</script>

<style scoped>
/* Scrolling Gallery */
.scrolling-gallery {
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  background: #000;
  z-index: 1;
}

.gallery-columns {
  display: flex;
  height: 100%;
  width: 100%;
  gap: 1rem;
  padding: 0 2rem;
}

.gallery-column {
  flex: 1;
  height: 100%;
  overflow: hidden;
}

.column-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  animation: scroll var(--scroll-speed, 120s) linear infinite;
  will-change: transform;
}

@keyframes scroll {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
}

.gallery-column:nth-child(2) .column-content {
  animation-delay: -20s;
}

.gallery-column:nth-child(3) .column-content {
  animation-delay: -40s;
}

.gallery-item {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.gallery-item:hover {
  transform: scale(1.02);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.image-container {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 2rem 1rem 1rem;
  transform: translateY(0);
  transition: all 0.3s ease;
}

.image-overlay.hover-only {
  transform: translateY(100%);
}

.gallery-item:hover .image-overlay.hover-only {
  transform: translateY(0);
}

.gallery-item:hover .image-overlay {
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
}

.gallery-item:hover .image-label {
  font-weight: 600;
  text-shadow: 0 3px 6px rgba(0, 0, 0, 0.7);
}

.image-label {
  color: white;
  font-size: 1rem;
  font-weight: 500;
  font-variant: small-caps;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

/* Pause animation on hover */
.gallery-column:hover .column-content {
  animation-play-state: paused;
}

/* Responsive Design */
@media (max-width: 768px) {
  .scrolling-gallery {
    height: 100vh;
  }
  
  .gallery-columns {
    padding: 0 1rem;
    gap: 0.5rem;
  }

  .image-overlay {
    padding: 1.25rem 0.6rem 0.6rem;
  }

  .image-label {
    font-size: 0.72rem;
    font-weight: 600;
  }
}

@media (max-width: 480px) {
  .image-overlay {
    padding: 1rem 0.5rem 0.5rem;
  }

  .image-label {
    font-size: 0.62rem;
  }
}
</style>
